import { gql } from '@apollo/client';
import React from 'react';

import {
  allFeatureFlagNames,
  enabledByDefaultFeatureFlags,
} from '@sbu/hooks.featureflag';

import {
  AppInfoOperationQuery as AppInfoOperation,
  AppInfoOperationQueryVariables as AppInfoOperationVariables,
} from 'core/graphql';
import { Loader } from 'shared/components/elements/Loader';
import { useQuery } from 'shared/hooks';

export const APP_INFO_QUERY = gql`
  fragment CurrentUserFragment on CurrentUser {
    createdTime
    email
    id
    name
    permissions
    preferredTimezone
    username
  }
  fragment CurrentUserSumoFragment on CurrentUserSumo {
    id
    accountType
    accountStatus
    organization
    organizationId
    userHashForIntercom
    userIdForIntercom
  }
  query AppInfoOperation(
    $featureFlagsToLookup: [String!]!
    $enabledByDefaultFeatureFlags: [String!]!
  ) {
    currentUser {
      ...CurrentUserFragment
    }
    currentUserSumo {
      ...CurrentUserSumoFragment
    }
    isFederatedCluster
    enabledFeatureFlags(
      featureFlagsToLookup: $featureFlagsToLookup
      enabledByDefaultFeatureFlags: $enabledByDefaultFeatureFlags
    )
    version
    sumoLogicRegion
    deployment
    orgId
    orgName
    cspCustomReportUrl
    sessionTimeoutMinutes
  }
`;

export type AppInfo = AppInfoOperation;

type AppInfoQueryProps = {
  showLoader?: boolean;
  children: (appInfo: AppInfo) => JSX.Element | null;
};

export const useAppInfoQuery = () => {
  const { data, loading, error } = useQuery<
    AppInfoOperation,
    AppInfoOperationVariables
  >(APP_INFO_QUERY, {
    notifyErrors: (error) => {
      const [graphQLError] = error?.graphQLErrors ?? [];
      return graphQLError.message !== 'Request not authorized';
    },
    variables: {
      featureFlagsToLookup: allFeatureFlagNames,
      enabledByDefaultFeatureFlags: enabledByDefaultFeatureFlags,
    },
  });

  return error ? { error } : { data, loading };
};

export const AppInfoQuery = ({ showLoader, children }: AppInfoQueryProps) => {
  const { data, loading } = useAppInfoQuery();

  if (loading && showLoader) {
    return <Loader />;
  }

  if (!loading && data) {
    return children(data);
  }

  return null;
};
